import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Calendar,
  Package,
  ShoppingBag,
  MessageSquare,
  Star,
  TrendingUp,
  Users,
  AlertTriangle,
  ArrowRight,
  Clock,
  Euro
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'RDV Aujourd\'hui',
      value: stats?.bookings?.today || 0,
      icon: Calendar,
      color: 'bg-blue-500',
      link: '/admin/bookings'
    },
    {
      title: 'RDV en attente',
      value: stats?.bookings?.pending || 0,
      icon: Clock,
      color: 'bg-yellow-500',
      link: '/admin/bookings'
    },
    {
      title: 'Commandes en attente',
      value: stats?.orders?.pending || 0,
      icon: Package,
      color: 'bg-orange-500',
      link: '/admin/orders'
    },
    {
      title: 'Chiffre d\'affaires',
      value: `${stats?.revenue?.toFixed(2) || 0}€`,
      icon: Euro,
      color: 'bg-green-500',
      link: '/admin/orders'
    },
    {
      title: 'Messages non lus',
      value: stats?.unreadMessages || 0,
      icon: MessageSquare,
      color: 'bg-purple-500',
      link: '/admin/messages'
    },
    {
      title: 'Témoignages en attente',
      value: stats?.pendingTestimonials || 0,
      icon: Star,
      color: 'bg-pink-500',
      link: '/admin/testimonials'
    },
    {
      title: 'Produits',
      value: stats?.products?.total || 0,
      icon: ShoppingBag,
      color: 'bg-indigo-500',
      link: '/admin/products'
    },
    {
      title: 'Stock faible',
      value: stats?.products?.lowStock || 0,
      icon: AlertTriangle,
      color: 'bg-red-500',
      link: '/admin/products'
    },
  ];

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      ready: 'bg-purple-100 text-purple-800'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmé',
      cancelled: 'Annulé',
      completed: 'Terminé',
      ready: 'Prêt'
    };
    return <Badge className={colors[status]}>{labels[status]}</Badge>;
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-[#2C1810]">Dashboard</h1>
        <p className="text-[#5D4037] mt-1">Bienvenue sur votre espace d'administration</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#5D4037]">{stat.title}</p>
                      <p className="text-3xl font-bold text-[#2C1810] mt-1">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-full ${stat.color} flex items-center justify-center`}>
                      <Icon className="text-white" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Bookings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#2C1810]">Derniers rendez-vous</CardTitle>
            <Link to="/admin/bookings" className="text-[#C9A96E] hover:underline text-sm flex items-center gap-1">
              Voir tout <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recentBookings?.length > 0 ? (
              <div className="space-y-4">
                {stats.recentBookings.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between p-3 bg-[#F5F0EB] rounded-lg">
                    <div>
                      <p className="font-medium text-[#2C1810]">{booking.firstName} {booking.lastName}</p>
                      <p className="text-sm text-[#5D4037]">{booking.serviceName}</p>
                      <p className="text-xs text-[#A67B5B]">{booking.date} à {booking.time}</p>
                    </div>
                    {getStatusBadge(booking.status)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#5D4037] text-center py-8">Aucun rendez-vous récent</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#2C1810]">Dernières commandes</CardTitle>
            <Link to="/admin/orders" className="text-[#C9A96E] hover:underline text-sm flex items-center gap-1">
              Voir tout <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recentOrders?.length > 0 ? (
              <div className="space-y-4">
                {stats.recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-[#F5F0EB] rounded-lg">
                    <div>
                      <p className="font-medium text-[#2C1810]">{order.customerName}</p>
                      <p className="text-sm text-[#5D4037]">{order.orderNumber}</p>
                      <p className="text-xs text-[#C9A96E] font-semibold">{order.total?.toFixed(2)}€</p>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#5D4037] text-center py-8">Aucune commande récente</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
